<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import CommMenusAPI from 'dashboard/api/commMenus';

// Generate a unique session ID for this browser session
const SESSION_ID_KEY = 'comm_menu_session_id';

function getOrCreateSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

const { t } = useI18n();
const router = useRouter();
const getters = useStoreGetters();
const store = useStore();

const isExpanded = ref(true);
const isLoading = ref(true);
const menu = ref(null);
const hasFetched = ref(false);
const shouldShow = ref(false);
const sessionId = ref(getOrCreateSessionId());
const dismissedInThisView = ref(false);
const completedItems = ref([]);

const currentAccountId = computed(() => {
  const currentUser = getters.getCurrentUser.value;
  return currentUser?.account_id;
});

// Check if this is a checklist type menu
const isChecklist = computed(() => menu.value?.content_type === 'checklist');

// Content type icons
const contentTypeIcons = {
  announcement: 'i-lucide-megaphone',
  changelog: 'i-lucide-git-branch',
  alert: 'i-lucide-alert-triangle',
  tip: 'i-lucide-lightbulb',
  checklist: 'i-lucide-list-checks',
};

// Content type gradients
const contentTypeGradients = {
  announcement: 'linear-gradient(135deg, #14b8a6 0%, #10b981 50%, #22c55e 100%)',
  changelog: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #4f46e5 100%)',
  alert: 'linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #ef4444 100%)',
  tip: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 50%, #14b8a6 100%)',
  checklist: 'linear-gradient(135deg, #10b981 0%, #14b8a6 50%, #06b6d4 100%)',
};

const menuIcon = computed(() => {
  if (!menu.value) return 'i-lucide-bell';
  return menu.value.icon || contentTypeIcons[menu.value.content_type] || 'i-lucide-bell';
});

const menuGradient = computed(() => {
  if (!menu.value) return contentTypeGradients.announcement;
  return contentTypeGradients[menu.value.content_type] || contentTypeGradients.announcement;
});

// Get actions sorted by button_style priority (primary first)
const sortedActions = computed(() => {
  if (!menu.value?.actions) return [];
  return [...menu.value.actions].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    const stylePriority = { primary: 0, secondary: 1, outline: 2, text: 3 };
    return (stylePriority[a.button_style] || 4) - (stylePriority[b.button_style] || 4);
  });
});

// Checklist items
const checklistItems = computed(() => menu.value?.checklist_items || []);

// Progress for checklist
const checklistProgress = computed(() => {
  if (!isChecklist.value) return { completed: 0, total: 0, percentage: 0 };
  const total = menu.value?.total_items || checklistItems.value.length;
  const completed = completedItems.value.length;
  return {
    completed,
    total,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
});

// Check if an item is completed
function isItemCompleted(itemId) {
  return completedItems.value.includes(itemId);
}

async function fetchCurrentMenu() {
  if (hasFetched.value || !currentAccountId.value) {
    return;
  }

  try {
    isLoading.value = true;
    hasFetched.value = true;
    dismissedInThisView.value = false;

    const { data } = await CommMenusAPI.current(sessionId.value);

    if (!data.menu) {
      shouldShow.value = false;
      isLoading.value = false;
      return;
    }

    menu.value = data.menu;
    completedItems.value = data.menu.completed_items || [];
    shouldShow.value = true;
  } catch (error) {
    console.error('Error fetching comm menu:', error);
    shouldShow.value = false;
  } finally {
    isLoading.value = false;
  }
}

async function handleAction(action) {
  if (!action) return;

  try {
    await CommMenusAPI.actionClicked(menu.value.id, action.id, sessionId.value);
  } catch (e) {
    console.error('Error tracking action click:', e);
  }

  if (action.type === 'internal_route') {
    router.push({ name: action.value });
    // For non-checklists, dismiss after action
    if (!isChecklist.value) {
      dismissMenu();
    }
  } else if (action.type === 'internal_url') {
    router.push(action.value);
    if (!isChecklist.value) {
      dismissMenu();
    }
  } else if (action.type === 'external_link') {
    window.open(action.value, '_blank');
  }
}

async function handleChecklistItemClick(item) {
  if (!item) return;

  // Navigate if there's an action
  if (item.action_type && item.action_value) {
    if (item.action_type === 'internal_route') {
      router.push({ name: item.action_value });
    } else if (item.action_type === 'internal_url') {
      router.push(item.action_value);
    } else if (item.action_type === 'external_link') {
      window.open(item.action_value, '_blank');
    }
  }

  // For manual completion trigger, mark as complete on click
  if (item.completion_trigger === 'manual' && !isItemCompleted(item.id)) {
    await completeChecklistItem(item.id);
  }
}

async function completeChecklistItem(itemId) {
  if (isItemCompleted(itemId)) return;

  try {
    const { data } = await CommMenusAPI.completeChecklistItem(
      menu.value.id,
      itemId,
      sessionId.value
    );
    
    completedItems.value = data.completed_items || [...completedItems.value, itemId];
    
    // If all items completed, hide after a delay
    if (data.all_completed) {
      setTimeout(() => {
        shouldShow.value = false;
      }, 1500);
    }
  } catch (e) {
    console.error('Error completing checklist item:', e);
  }
}

async function dismissMenu() {
  if (!menu.value) return;

  dismissedInThisView.value = true;
  shouldShow.value = false;

  try {
    await CommMenusAPI.dismiss(menu.value.id, sessionId.value);
  } catch (e) {
    console.error('Error dismissing menu:', e);
  }
}

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}

function getButtonStyle(action) {
  const base = 'w-full py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2';
  
  switch (action.button_style) {
    case 'primary':
      return {
        class: base,
        style: `background: ${menuGradient.value}; color: white; box-shadow: 0 4px 14px 0 rgba(16, 185, 129, 0.25);`
      };
    case 'secondary':
      return {
        class: base,
        style: 'background-color: #f3f4f6; color: #374151;'
      };
    case 'outline':
      return {
        class: base,
        style: 'background-color: transparent; color: #374151; border: 1px solid #d1d5db;'
      };
    case 'text':
      return {
        class: `${base} py-2`,
        style: 'background-color: transparent; color: #6b7280;'
      };
    default:
      return {
        class: base,
        style: 'background-color: #f3f4f6; color: #374151;'
      };
  }
}

// Watch for inboxes to auto-complete inbox_created triggers
const inboxes = computed(() => getters['inboxes/getInboxes'].value);

watch(inboxes, (newInboxes, oldInboxes) => {
  if (!isChecklist.value || !menu.value) return;
  
  // Check if a new inbox was created
  if (newInboxes?.length > (oldInboxes?.length || 0)) {
    // Find items with inbox_created trigger
    checklistItems.value.forEach(item => {
      if (item.completion_trigger === 'inbox_created' && !isItemCompleted(item.id)) {
        completeChecklistItem(item.id);
      }
    });
  }
}, { deep: true });

// Watch for route changes to auto-complete route_visited triggers
const currentRoute = computed(() => router.currentRoute.value);

watch(currentRoute, (newRoute) => {
  if (!isChecklist.value || !menu.value) return;
  
  const currentPath = newRoute.path;
  const currentName = newRoute.name;
  
  // Check all checklist items with route_visited trigger
  checklistItems.value.forEach(item => {
    if (item.completion_trigger === 'route_visited' && !isItemCompleted(item.id)) {
      const targetValue = item.completion_value || item.action_value;
      
      if (targetValue) {
        // Check if current route matches (by path or name)
        const matchesPath = currentPath && currentPath.includes(targetValue);
        const matchesName = currentName && currentName === targetValue;
        
        if (matchesPath || matchesName) {
          completeChecklistItem(item.id);
        }
      }
    }
  });
}, { immediate: true });

watch(currentAccountId, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal && !hasFetched.value) {
    fetchCurrentMenu();
  }
});

onMounted(() => {
  setTimeout(() => {
    if (currentAccountId.value && !hasFetched.value) {
      fetchCurrentMenu();
    }
  }, 100);
});
</script>

<template>
  <Transition name="slide-up">
    <div
      v-if="shouldShow && menu && !isLoading && !dismissedInThisView"
      class="fixed bottom-6 right-6 z-50"
      :style="{ maxWidth: isChecklist ? '400px' : '380px', width: '100%' }"
    >
      <!-- Regular Menu (non-checklist) -->
      <template v-if="!isChecklist">
        <!-- Collapsed State -->
        <div
          v-if="!isExpanded"
          class="cursor-pointer rounded-2xl p-4 shadow-lg border transition-all duration-300 hover:shadow-xl"
          style="background-color: white; border-color: #e5e7eb"
          @click="toggleExpanded"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              :style="{ background: menuGradient }"
            >
              <span :class="[menuIcon, 'w-5 h-5 text-white']" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-sm truncate" style="color: #111827">
                {{ menu.title }}
              </p>
              <p class="text-xs" style="color: #6b7280">
                {{ $t('COMM_MENU.CLICK_TO_EXPAND') }}
              </p>
            </div>
            <span class="i-lucide-chevron-up w-5 h-5" style="color: #6b7280" />
          </div>
        </div>

        <!-- Expanded State -->
        <div
          v-else
          class="rounded-2xl shadow-xl border overflow-hidden"
          style="background-color: white; border-color: #e5e7eb"
        >
          <!-- Header -->
          <div class="p-4 relative" :style="{ background: menuGradient }">
            <button
              class="absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              style="background-color: rgba(255, 255, 255, 0.2)"
              @click="toggleExpanded"
            >
              <span class="i-lucide-chevron-down w-4 h-4 text-white" />
            </button>
            <div class="flex items-center gap-3 pr-10">
              <div
                class="w-12 h-12 rounded-xl flex items-center justify-center"
                style="background-color: rgba(255, 255, 255, 0.2)"
              >
                <span :class="[menuIcon, 'w-6 h-6 text-white']" />
              </div>
              <div>
                <span
                  class="inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-1"
                  style="background-color: rgba(255, 255, 255, 0.2); color: white"
                >
                  {{ $t(`COMM_MENU.TYPES.${menu.content_type.toUpperCase()}`) }}
                </span>
                <h3 class="font-bold text-white text-base leading-tight">
                  {{ menu.title }}
                </h3>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="p-4">
            <p class="text-sm leading-relaxed" style="color: #4b5563">
              {{ menu.description }}
            </p>
          </div>

          <!-- Actions -->
          <div class="px-4 pb-4 space-y-2">
            <button
              v-for="action in sortedActions"
              :key="action.id"
              :class="getButtonStyle(action).class"
              :style="getButtonStyle(action).style"
              @click="handleAction(action)"
            >
              <span v-if="action.icon" :class="[action.icon, 'w-4 h-4']" />
              <span>{{ action.label }}</span>
              <span
                v-if="action.type === 'external_link'"
                class="i-lucide-external-link w-4 h-4"
              />
              <span v-else class="i-lucide-arrow-right w-4 h-4" />
            </button>

            <!-- Dismiss -->
            <button
              class="w-full py-2 rounded-lg text-xs transition-colors"
              style="color: #9ca3af"
              @click="dismissMenu"
            >
              {{ $t('COMM_MENU.DISMISS') }}
            </button>
          </div>
        </div>
      </template>

      <!-- Checklist Menu -->
      <template v-else>
        <!-- Collapsed Checklist -->
        <div
          v-if="!isExpanded"
          class="cursor-pointer rounded-2xl p-4 shadow-lg border transition-all duration-300 hover:shadow-xl"
          style="background-color: white; border-color: #e5e7eb"
          @click="toggleExpanded"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              :style="{ background: menuGradient }"
            >
              <span :class="[menuIcon, 'w-5 h-5 text-white']" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-sm truncate" style="color: #111827">
                {{ menu.title }}
              </p>
              <div class="flex items-center gap-2 mt-1">
                <div class="flex-1 h-1.5 rounded-full overflow-hidden" style="background-color: #e5e7eb">
                  <div
                    class="h-full rounded-full transition-all duration-300"
                    :style="{ width: `${checklistProgress.percentage}%`, background: menuGradient }"
                  />
                </div>
                <span class="text-xs font-medium" style="color: #6b7280">
                  {{ checklistProgress.completed }}/{{ checklistProgress.total }}
                </span>
              </div>
            </div>
            <span class="i-lucide-chevron-up w-5 h-5" style="color: #6b7280" />
          </div>
        </div>

        <!-- Expanded Checklist -->
        <div
          v-else
          class="rounded-2xl shadow-xl border overflow-hidden"
          style="background-color: white; border-color: #e5e7eb"
        >
          <!-- Header -->
          <div class="p-4 relative" :style="{ background: menuGradient }">
            <button
              class="absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              style="background-color: rgba(255, 255, 255, 0.2)"
              @click="toggleExpanded"
            >
              <span class="i-lucide-chevron-down w-4 h-4 text-white" />
            </button>
            <div class="flex items-center gap-3 pr-10">
              <div
                class="w-12 h-12 rounded-xl flex items-center justify-center"
                style="background-color: rgba(255, 255, 255, 0.2)"
              >
                <span :class="[menuIcon, 'w-6 h-6 text-white']" />
              </div>
              <div class="flex-1">
                <h3 class="font-bold text-white text-base leading-tight">
                  {{ menu.title }}
                </h3>
                <p class="text-white/80 text-xs mt-0.5">
                  {{ checklistProgress.completed }} de {{ checklistProgress.total }} {{ $t('COMM_MENU.CHECKLIST.COMPLETED') }}
                </p>
              </div>
            </div>
            <!-- Progress bar -->
            <div class="mt-3 h-1.5 rounded-full overflow-hidden" style="background-color: rgba(255, 255, 255, 0.2)">
              <div
                class="h-full rounded-full transition-all duration-500"
                style="background-color: white"
                :style="{ width: `${checklistProgress.percentage}%` }"
              />
            </div>
          </div>

          <!-- Description (if any) -->
          <div v-if="menu.description" class="px-4 pt-4">
            <p class="text-sm leading-relaxed" style="color: #6b7280">
              {{ menu.description }}
            </p>
          </div>

          <!-- Checklist Items -->
          <div class="p-4 space-y-2">
            <div
              v-for="item in checklistItems"
              :key="item.id"
              class="group flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer"
              :style="{
                backgroundColor: isItemCompleted(item.id) ? '#f0fdf4' : '#f9fafb',
                borderLeft: isItemCompleted(item.id) ? '3px solid #10b981' : '3px solid transparent'
              }"
              @click="handleChecklistItemClick(item)"
            >
              <!-- Checkbox -->
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200"
                :style="{
                  backgroundColor: isItemCompleted(item.id) ? '#10b981' : 'white',
                  border: isItemCompleted(item.id) ? 'none' : '2px solid #d1d5db'
                }"
              >
                <span
                  v-if="isItemCompleted(item.id)"
                  class="i-lucide-check w-4 h-4 text-white"
                />
              </div>
              
              <!-- Content -->
              <div class="flex-1 min-w-0">
                <p
                  class="font-medium text-sm transition-all duration-200"
                  :style="{
                    color: isItemCompleted(item.id) ? '#059669' : '#111827',
                    textDecoration: isItemCompleted(item.id) ? 'line-through' : 'none'
                  }"
                >
                  {{ item.title }}
                </p>
                <p
                  v-if="item.description"
                  class="text-xs mt-0.5"
                  :style="{ color: isItemCompleted(item.id) ? '#6ee7b7' : '#6b7280' }"
                >
                  {{ item.description }}
                </p>
              </div>

              <!-- Action indicator -->
              <div
                v-if="item.action_value && !isItemCompleted(item.id)"
                class="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <span
                  v-if="item.action_type === 'external_link'"
                  class="i-lucide-external-link w-4 h-4"
                  style="color: #9ca3af"
                />
                <span
                  v-else
                  class="i-lucide-arrow-right w-4 h-4"
                  style="color: #9ca3af"
                />
              </div>
            </div>
          </div>

          <!-- Dismiss for checklist (optional) -->
          <div class="px-4 pb-4">
            <button
              class="w-full py-2 rounded-lg text-xs transition-colors"
              style="color: #9ca3af"
              @click="dismissMenu"
            >
              {{ $t('COMM_MENU.CHECKLIST.HIDE_FOR_NOW') }}
            </button>
          </div>
        </div>
      </template>
    </div>
  </Transition>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
